/**
 * @file Modal for creating a new organization and inviting users.
 * Admin stays on their current org — the invited user handles onboarding.
 */

import { useState, useCallback, useRef } from 'react'
import { useOrganizationList } from '@clerk/clerk-react'
import { Loader2, Plus, Trash2, Send } from 'lucide-react'

import {
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  toast,
} from '@strivelabs/ui-core'
import { Button, IconButton } from '@strivelabs/ui-core/v2'

import { logger, logInfo } from '@/lib/logger'
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics'

const INVITE_ROLES = [
  { value: 'org:admin', label: 'Admin' },
  { value: 'org:member', label: 'Member' },
] as const

interface InviteEntry {
  email: string
  role: string
}

interface CreatedOrg {
  id: string
  inviteMember: (args: { emailAddress: string; role: string }) => Promise<unknown>
}

interface AddAccountModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddAccountModal({ open, onOpenChange }: AddAccountModalProps) {
  const { createOrganization, userMemberships } = useOrganizationList()
  const trackEvent = useTrack()

  // Step: 'name' or 'invite'
  const [step, setStep] = useState<'name' | 'invite'>('name')
  const [orgName, setOrgName] = useState('')
  const createdOrgRef = useRef<CreatedOrg | null>(null)

  // Invite list
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('org:admin')
  const [invites, setInvites] = useState<InviteEntry[]>([])

  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetAndClose = useCallback(() => {
    setStep('name')
    setOrgName('')
    createdOrgRef.current = null
    setInviteEmail('')
    setInviteRole('org:admin')
    setInvites([])
    onOpenChange(false)
  }, [onOpenChange])

  const handleCreateOrg = useCallback(async () => {
    const name = orgName.trim()
    if (!name || !createOrganization) return

    setIsSubmitting(true)
    try {
      const org = await createOrganization({ name })
      trackEvent(ANALYTICS_EVENTS.ACCOUNT_ADDED, {})
      logInfo('AddAccountModal: Created organization', { orgId: org.id, orgName: name })

      createdOrgRef.current = org as unknown as CreatedOrg
      setStep('invite')
    } catch (error) {
      logger(
        error instanceof Error ? error : new Error('Failed to create organization', { cause: error }),
        { context: 'AddAccountModal: Failed to create organization', orgName: name }
      )
      toast({
        title: 'Error',
        description: 'Failed to create organization. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [orgName, createOrganization, trackEvent])

  const addInviteToList = useCallback(() => {
    const email = inviteEmail.trim()
    if (!email) return
    if (invites.some((i) => i.email === email)) {
      toast({ title: 'Duplicate', description: `${email} is already in the list.` })
      return
    }
    setInvites((prev) => [...prev, { email, role: inviteRole }])
    setInviteEmail('')
  }, [inviteEmail, inviteRole, invites])

  const removeInvite = useCallback((email: string) => {
    setInvites((prev) => prev.filter((i) => i.email !== email))
  }, [])

  const handleSendInvites = useCallback(async () => {
    const org = createdOrgRef.current
    if (!org) return

    // Auto-include email from the input field if present
    const allInvites = [...invites]
    const pendingEmail = inviteEmail.trim()
    if (pendingEmail && !allInvites.some((i) => i.email === pendingEmail)) {
      allInvites.push({ email: pendingEmail, role: inviteRole })
    }
    if (allInvites.length === 0) return

    setIsSubmitting(true)
    try {
      // Use Clerk's frontend SDK directly — no backend needed
      const results = await Promise.allSettled(
        allInvites.map((inv) => org.inviteMember({ emailAddress: inv.email, role: inv.role }))
      )

      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failedResults = allInvites.filter((_, i) => results[i].status === 'rejected')

      if (succeeded > 0) {
        toast({
          title: 'Invites sent',
          description: `${succeeded} invitation${succeeded > 1 ? 's' : ''} sent successfully.${failedResults.length > 0 ? ` ${failedResults.length} failed.` : ''}`,
        })
      }

      // Keep failed invites in the list so user can retry
      if (failedResults.length > 0) {
        setInvites(failedResults)
        setInviteEmail('')
        if (succeeded === 0) {
          toast({
            title: 'Error',
            description: 'Failed to send invitations. Please try again.',
            variant: 'destructive',
          })
        }
        setIsSubmitting(false)
        return
      }

      logInfo('AddAccountModal: Invites sent', { orgId: org.id, succeeded })

      // Reload to refresh the org list in the switcher
      window.location.reload()
    } catch (error) {
      logger(
        error instanceof Error ? error : new Error('Failed to send invitations', { cause: error }),
        { context: 'AddAccountModal: Failed to send invitations' }
      )
      toast({
        title: 'Error',
        description: 'Failed to send invitations. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [invites, inviteEmail, inviteRole, userMemberships, resetAndClose])

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetAndClose() }}>
      <DialogContent className="sm:max-w-[480px]">
        {step === 'name' && (
          <>
            <DialogHeader>
              <DialogTitle>Create New Organization</DialogTitle>
              <DialogDescription>
              You can invite users in the next step
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleCreateOrg() }
                }}
                placeholder="Organization name"
                disabled={isSubmitting}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={resetAndClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleCreateOrg} disabled={!orgName.trim() || isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Create
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'invite' && (
          <>
            <DialogHeader>
              <DialogTitle>Invite Users</DialogTitle>
              <DialogDescription>
                Add users to the <span className="font-medium">{orgName || 'new Organization'}</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              {/* Email + role input row */}
              <div className="flex items-center gap-2">
                <Input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addInviteToList() }
                  }}
                  placeholder="user@email.com"
                  type="email"
                  disabled={isSubmitting}
                  className="flex-1"
                  autoFocus
                />
                <Select value={inviteRole} onValueChange={setInviteRole} disabled={isSubmitting}>
                  <SelectTrigger className="h-9 w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVITE_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <span className='ml-4'>{role.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <IconButton variant="secondary" size="md"
                  type="button"
                  aria-label="Add to invite list"
                  onClick={addInviteToList}
                  disabled={!inviteEmail.trim() || isSubmitting}
                >
                  <Plus className="h-4 w-4" />
                </IconButton>
              </div>

              {/* Invite list */}
              {invites.length > 0 && (
                <div className="rounded-md border border-border divide-y divide-border">
                  {invites.map((inv) => (
                    <div key={inv.email} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate">{inv.email}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {INVITE_ROLES.find((r) => r.value === inv.role)?.label}
                        </span>
                      </div>
                      <IconButton size="2xs" tone="danger"
                        type="button"
                        aria-label={`Remove ${inv.email}`}
                        onClick={() => removeInvite(inv.email)}
                        disabled={isSubmitting}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="borderless" onClick={() => {
                window.location.reload()
              }} disabled={isSubmitting}>
                Skip
              </Button>
              <Button
                onClick={handleSendInvites}
                disabled={isSubmitting || (invites.length === 0 && !inviteEmail.trim())}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {invites.length > 0
                  ? `Send ${invites.length} Invite${invites.length > 1 ? 's' : ''}`
                  : 'Send Invite'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
